/**
 * Mini-games played on the login's particle field.
 *
 * The field (`NlParticlesComponent`) is the host: it steps the active game at
 * 60Hz, draws it over the particles and forwards the pointer. A game only owns
 * its own state and an O(n) pass over the particles at most — no pair loops —
 * so the field keeps its frame rate whatever is being played. Snake lives in
 * the component; everything else is here.
 */

export type GameId = "snake" | "hunter" | "constellation" | "pinball" | "survival" | "chain" | "gravity";

/** Menu entries, in order. `icon` is a Material Symbols name. */
export const GAMES: { id: GameId; icon: string }[] = [
  { id: "snake", icon: "route" },
  { id: "hunter", icon: "bolt" },
  { id: "constellation", icon: "hub" },
  { id: "pinball", icon: "sports_baseball" },
  { id: "survival", icon: "shield" },
  { id: "chain", icon: "flare" },
  { id: "gravity", icon: "blur_circular" },
];

/** The part of a field particle the games touch. */
export interface GameParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  glow: number;
  formed: boolean;
  tx: number;
  ty: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** What the field offers a game. Coordinates are stage pixels. */
export interface GameHost {
  readonly width: number;
  readonly height: number;
  readonly particles: GameParticle[];
  readonly color: string;
  /** steps since the field started */
  readonly tick: number;
  readonly mouse: { x: number; y: number; active: boolean; vx: number; vy: number };
  /** the pointer is down on the stage */
  readonly held: boolean;
  /** the login card, where nothing can be clicked or seen (cached, cheap to ask) */
  card(): Rect | null;
  label(key: string): string;
  /** a big strike out of (x, y) */
  lightning(x: number, y: number): void;
  /** tops the field back up with up to `n` new particles */
  refill(n?: number): void;
  /** pins the nearest free particles to the dots, in dot order */
  lock(dots: [number, number][]): GameParticle[];
  unlock(): void;
}

export interface Game {
  readonly id: GameId;
  start(): void;
  stop(): void;
  step(): void;
  draw(ctx: CanvasRenderingContext2D): void;
  pointerDown(x: number, y: number): void;
  pointerUp(x: number, y: number): void;
}

// ------------------------------------------------------------------ helpers

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const GOLD = "#ffd24a";
const RED = "#ff3b4e";
const TAU = Math.PI * 2;

const bestKey = (id: GameId) => `nl-game-best-${id}`;

const readBest = (id: GameId): number => {
  try {
    return Number(localStorage.getItem(bestKey(id))) || 0;
  } catch {
    return 0;
  }
};

const writeBest = (id: GameId, v: number): void => {
  try {
    localStorage.setItem(bestKey(id), String(v));
  } catch {
    // private mode: the record just lasts this game
  }
};

/** A random spot on the stage, clear of the login card. */
function freePoint(h: GameHost, margin = 60): [number, number] {
  const card = h.card();
  for (let i = 0; i < 40; i++) {
    const x = margin + Math.random() * (h.width - margin * 2);
    const y = margin + Math.random() * (h.height - margin * 2);
    if (!card || x < card.x - 40 || x > card.x + card.w + 40 || y < card.y - 40 || y > card.y + card.h + 40) {
      return [x, y];
    }
  }
  return [margin, margin];
}

function glowDot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string, glow: string, blur = 16): void {
  ctx.shadowColor = glow;
  ctx.shadowBlur = blur;
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.shadowBlur = 0;
}

/** Closed path (n + 1 points, the last is the first) of a regular polygon, or of the star that skips a vertex. Odd `n` only for stars. */
export function shapePath(cx: number, cy: number, r: number, n: number, star: boolean): [number, number][] {
  const step = star ? 2 : 1;
  const path = Array.from({ length: n }, (_, i): [number, number] => {
    const a = -Math.PI / 2 + (((i * step) % n) * TAU) / n;
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
  });
  path.push(path[0]);
  return path;
}

/** Bounces a ball off a rectangle in place. Returns whether it touched. */
export function bounceRect(b: { x: number; y: number; vx: number; vy: number; r: number }, rc: Rect, e = 0.9): boolean {
  const cx = Math.max(rc.x, Math.min(b.x, rc.x + rc.w));
  const cy = Math.max(rc.y, Math.min(b.y, rc.y + rc.h));
  let dx = b.x - cx;
  let dy = b.y - cy;
  const d2 = dx * dx + dy * dy;
  if (d2 >= b.r * b.r) {
    return false;
  }
  if (d2 === 0) {
    // centre inside the rectangle: leave through the nearest edge
    const left = b.x - rc.x;
    const right = rc.x + rc.w - b.x;
    const top = b.y - rc.y;
    const bottom = rc.y + rc.h - b.y;
    const m = Math.min(left, right, top, bottom);
    if (m === left) {
      dx = -1;
      dy = 0;
      b.x = rc.x - b.r;
    } else if (m === right) {
      dx = 1;
      dy = 0;
      b.x = rc.x + rc.w + b.r;
    } else if (m === top) {
      dx = 0;
      dy = -1;
      b.y = rc.y - b.r;
    } else {
      dx = 0;
      dy = 1;
      b.y = rc.y + rc.h + b.r;
    }
  } else {
    const d = Math.sqrt(d2);
    dx /= d;
    dy /= d;
    b.x = cx + dx * b.r;
    b.y = cy + dy * b.r;
  }
  const vn = b.vx * dx + b.vy * dy;
  if (vn < 0) {
    b.vx -= (1 + e) * vn * dx;
    b.vy -= (1 + e) * vn * dy;
  }
  return true;
}

// ------------------------------------------------------------------ base

/** Score, record, lives-free game-over card and restart-on-click, shared by every game. */
abstract class BaseGame implements Game {
  score = 0;
  best = 0;
  /** 0 while playing; counts the steps since the end (drives the card) */
  over = 0;

  protected constructor(
    protected readonly h: GameHost,
    readonly id: GameId,
  ) {}

  start(): void {
    this.best = readBest(this.id);
    this.score = 0;
    this.over = 0;
    this.init();
  }

  stop(): void {
    this.cleanup();
  }

  step(): void {
    if (this.over) {
      this.over++;
      return;
    }
    this.update();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    this.render(ctx);
    ctx.restore();
    this.hud(ctx);
  }

  pointerDown(x: number, y: number): void {
    if (this.over > 40) {
      this.cleanup();
      this.start();
    } else if (!this.over) {
      this.down(x, y);
    }
  }

  pointerUp(x: number, y: number): void {
    if (!this.over) {
      this.up(x, y);
    }
  }

  protected abstract init(): void;
  protected abstract update(): void;
  protected abstract render(ctx: CanvasRenderingContext2D): void;
  protected down(_x: number, _y: number): void {
    // most games act on the pointer inside `update`
  }
  protected up(_x: number, _y: number): void {
    // most games act on the pointer inside `update`
  }
  protected cleanup(): void {
    // nothing to release by default
  }
  /** What the HUD shows between the score and the record. */
  protected extra(): string {
    return "";
  }

  protected finish(): void {
    this.over = 1;
    if (this.score > this.best) {
      this.best = this.score;
      writeBest(this.id, this.best);
    }
  }

  private hud(ctx: CanvasRenderingContext2D): void {
    const L = (k: string) => this.h.label(k).toUpperCase();
    ctx.save();
    ctx.font = `600 12px ${MONO}`;
    ctx.fillStyle = this.h.color;
    ctx.globalAlpha = 0.85;
    const mid = this.extra();
    ctx.fillText(`${L("GENERAL.SNAKE_SCORE")} ${this.score}   ${mid ? mid + "   " : ""}${L("GENERAL.SNAKE_BEST")} ${this.best}`, 20, 28);
    if (this.over) {
      ctx.textAlign = "center";
      ctx.globalAlpha = Math.min(1, this.over / 30);
      ctx.font = `700 30px ${MONO}`;
      ctx.fillText(`${L("GENERAL.SNAKE_OVER")} · ${this.score}`, this.h.width / 2, this.h.height * 0.2);
      ctx.font = `500 13px ${MONO}`;
      ctx.globalAlpha *= 0.8;
      ctx.fillText(this.h.label("GENERAL.GAME_AGAIN"), this.h.width / 2, this.h.height * 0.2 + 26);
    }
    ctx.restore();
  }
}

// ------------------------------------------------------------------ 1. lightning hunter

interface Target {
  x: number;
  y: number;
  life: number;
  max: number;
}

/** Golden targets appear and vanish; click them in time. Three misses end it. */
class HunterGame extends BaseGame {
  private targets: Target[] = [];
  private rings: { x: number; y: number; age: number }[] = [];
  private lives = 3;
  private combo = 0;
  private level = 1;
  private spawnIn = 40;

  constructor(h: GameHost) {
    super(h, "hunter");
  }

  protected init(): void {
    this.targets = [];
    this.rings = [];
    this.lives = 3;
    this.combo = 0;
    this.level = 1;
    this.spawnIn = 40;
  }

  protected extra(): string {
    return `${this.h.label("GENERAL.GAME_LIVES").toUpperCase()} ${"♥".repeat(this.lives)}${this.combo > 1 ? `   x${this.combo}` : ""}`;
  }

  protected update(): void {
    const slots = this.level >= 4 ? 2 : 1;
    if (--this.spawnIn <= 0 && this.targets.length < slots) {
      const [x, y] = freePoint(this.h);
      const max = Math.max(60, 150 - this.level * 10);
      this.targets.push({ x, y, life: max, max });
      this.spawnIn = Math.max(20, 80 - this.level * 6);
    }
    for (let i = this.targets.length - 1; i >= 0; i--) {
      if (--this.targets[i].life <= 0) {
        this.targets.splice(i, 1);
        this.combo = 0;
        if (--this.lives <= 0) {
          this.finish();
          return;
        }
      }
    }
    for (let i = this.rings.length - 1; i >= 0; i--) {
      if (++this.rings[i].age > 30) this.rings.splice(i, 1);
    }
  }

  protected override down(x: number, y: number): void {
    const hit = this.targets.findIndex((t) => Math.hypot(t.x - x, t.y - y) < 38);
    if (hit < 0) {
      this.combo = 0;
      return;
    }
    const t = this.targets[hit];
    this.targets.splice(hit, 1);
    this.combo++;
    this.score += 1 + Math.floor(this.combo / 4);
    this.level = 1 + Math.floor(this.score / 8);
    this.rings.push({ x: t.x, y: t.y, age: 0 });
    this.h.lightning(t.x, t.y);
  }

  protected render(ctx: CanvasRenderingContext2D): void {
    for (const r of this.rings) {
      ctx.strokeStyle = GOLD;
      ctx.globalAlpha = 1 - r.age / 30;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(r.x, r.y, 10 + r.age * 3, 0, TAU);
      ctx.stroke();
    }
    for (const t of this.targets) {
      const pulse = 1 + Math.sin(this.h.tick * 0.2) * 0.2;
      glowDot(ctx, t.x, t.y, 6 * pulse, "#ffffff", GOLD, 20);
      ctx.globalAlpha = 0.9;
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = 2;
      // the ring is the time left: it closes like a clock
      ctx.beginPath();
      ctx.arc(t.x, t.y, 22, -Math.PI / 2, -Math.PI / 2 + TAU * (t.life / t.max));
      ctx.stroke();
    }
  }
}

// ------------------------------------------------------------------ 2. constellation

/** Particles pin themselves to the vertices of a shape; drag through them in order before the time runs out. */
class ConstellationGame extends BaseGame {
  private path: [number, number][] = [];
  private level = 1;
  private idx = 0;
  private dragging = false;
  private time = 0;
  private limit = 1;

  constructor(h: GameHost) {
    super(h, "constellation");
  }

  protected init(): void {
    this.level = 1;
    this.round();
  }

  protected override cleanup(): void {
    this.h.unlock();
  }

  protected extra(): string {
    return `${this.h.label("GENERAL.GAME_ROUND").toUpperCase()} ${this.level}`;
  }

  /** A new shape on whichever side of the card has room. */
  private round(): void {
    const n = 3 + ((this.level - 1) % 6);
    const star = n >= 5 && n % 2 === 1 && this.level % 2 === 0;
    const card = this.h.card();
    const w = this.h.width;
    const left = card ? card.x : w / 2;
    const right = card ? w - (card.x + card.w) : w / 2;
    const useLeft = left > right ? Math.random() < 0.75 : Math.random() < 0.25;
    const room = useLeft ? left : right;
    const cx = useLeft ? room / 2 : w - room / 2;
    const r = Math.max(60, Math.min(this.h.height * 0.28, room * 0.4));
    this.path = shapePath(cx, this.h.height * (0.42 + Math.random() * 0.16), r, n, star);
    this.h.unlock();
    this.h.lock(this.path.slice(0, n));
    this.idx = 0;
    this.dragging = false;
    this.limit = Math.max(300, (7 + n * 1.3 - this.level * 0.2) * 60);
    this.time = this.limit;
  }

  protected override down(x: number, y: number): void {
    if (Math.hypot(this.path[0][0] - x, this.path[0][1] - y) < 34) {
      this.dragging = true;
      this.idx = 1;
    }
  }

  protected override up(): void {
    this.dragging = false;
    this.idx = 0;
  }

  protected update(): void {
    if (--this.time <= 0) {
      this.finish();
      return;
    }
    if (!this.dragging) {
      return;
    }
    if (!this.h.held) {
      this.up();
      return;
    }
    const m = this.h.mouse;
    const next = this.path[this.idx];
    if (Math.hypot(next[0] - m.x, next[1] - m.y) < 30) {
      if (++this.idx >= this.path.length) {
        this.score++;
        this.level++;
        this.round();
      }
      return;
    }
    // touching a node that is not the next one, out of order, is a wrong turn: back to the start, and the clock pays
    for (let i = this.idx + 1; i < this.path.length - 1; i++) {
      if (Math.hypot(this.path[i][0] - m.x, this.path[i][1] - m.y) < 22) {
        this.time -= 90;
        this.up();
        return;
      }
    }
  }

  protected render(ctx: CanvasRenderingContext2D): void {
    const c = this.h.color;
    ctx.lineCap = "round";
    // the guide: faint, so the player knows the shape but has to draw it
    ctx.strokeStyle = c;
    ctx.globalAlpha = 0.18;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    this.path.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
    ctx.setLineDash([]);

    if (this.dragging && this.idx > 0) {
      ctx.strokeStyle = "#ffffff";
      ctx.shadowColor = c;
      ctx.shadowBlur = 14;
      ctx.globalAlpha = 0.95;
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      for (let i = 0; i < this.idx; i++) {
        const [x, y] = this.path[i];
        if (i) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
      }
      ctx.lineTo(this.h.mouse.x, this.h.mouse.y);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    const n = this.path.length - 1;
    for (let i = 0; i < n; i++) {
      const [x, y] = this.path[i];
      const target = this.dragging ? i === this.idx % n : i === 0;
      ctx.globalAlpha = target ? 1 : 0.6;
      ctx.strokeStyle = target ? "#ffffff" : c;
      ctx.lineWidth = target ? 2 : 1.2;
      ctx.beginPath();
      ctx.arc(x, y, target ? 14 + Math.sin(this.h.tick * 0.25) * 2 : 10, 0, TAU);
      ctx.stroke();
    }

    // time left
    const bw = 220;
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = c;
    ctx.fillRect(20, 40, bw, 3);
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = this.time < 120 ? RED : c;
    ctx.fillRect(20, 40, bw * (this.time / this.limit), 3);
  }
}

// ------------------------------------------------------------------ 3. pinball

interface Bumper {
  x: number;
  y: number;
  r: number;
  hit: number;
}

/** A ball under gravity; the cursor is the flipper. The login card is a wall. */
class PinballGame extends BaseGame {
  private ball = { x: 0, y: 0, vx: 0, vy: 0, r: 9 };
  private trail: number[] = [];
  private bumpers: Bumper[] = [];
  private lives = 3;
  private level = 1;

  constructor(h: GameHost) {
    super(h, "pinball");
  }

  protected init(): void {
    this.lives = 3;
    this.level = 1;
    this.bumpers = Array.from({ length: 6 }, () => {
      const [x, y] = freePoint(this.h, 80);
      return { x, y, r: 24, hit: 0 };
    });
    this.launch();
  }

  protected extra(): string {
    return `${this.h.label("GENERAL.GAME_LIVES").toUpperCase()} ${"♥".repeat(this.lives)}`;
  }

  private launch(): void {
    this.ball.x = this.h.width * (0.15 + Math.random() * 0.7);
    this.ball.y = 40;
    this.ball.vx = (Math.random() - 0.5) * 4;
    this.ball.vy = 1;
    this.trail = [];
  }

  protected update(): void {
    const b = this.ball;
    const w = this.h.width;
    b.vy += 0.1 + this.level * 0.01;
    b.x += b.vx;
    b.y += b.vy;

    // the cursor is the flipper: it throws the ball away, harder when it is moving
    const m = this.h.mouse;
    if (m.active) {
      const dx = b.x - m.x;
      const dy = b.y - m.y;
      const d = Math.hypot(dx, dy);
      const reach = 56 + b.r;
      if (d < reach && d > 0.001) {
        const f = (1 - d / reach) * 1.6 + Math.min(4, Math.hypot(m.vx, m.vy) * 0.25);
        b.vx += (dx / d) * f;
        b.vy += (dy / d) * f - 0.2;
      }
    }

    if (b.x < b.r) {
      b.x = b.r;
      b.vx = Math.abs(b.vx) * 0.95;
    } else if (b.x > w - b.r) {
      b.x = w - b.r;
      b.vx = -Math.abs(b.vx) * 0.95;
    }
    if (b.y < b.r) {
      b.y = b.r;
      b.vy = Math.abs(b.vy) * 0.95;
    }
    const card = this.h.card();
    if (card) {
      bounceRect(b, card);
    }
    const sp = Math.hypot(b.vx, b.vy);
    if (sp > 13) {
      b.vx *= 13 / sp;
      b.vy *= 13 / sp;
    }

    for (const t of this.bumpers) {
      if (t.hit > 0) t.hit--;
      const dx = b.x - t.x;
      const dy = b.y - t.y;
      const d = Math.hypot(dx, dy);
      if (d < b.r + t.r && d > 0.001) {
        // bounce off with a kick, then move the bumper elsewhere
        const nx = dx / d;
        const ny = dy / d;
        const vn = b.vx * nx + b.vy * ny;
        if (vn < 0) {
          b.vx -= 2.2 * vn * nx;
          b.vy -= 2.2 * vn * ny;
        }
        this.score++;
        this.level = 1 + Math.floor(this.score / 6);
        t.hit = 14;
        [t.x, t.y] = freePoint(this.h, 80);
        t.r = Math.max(14, 24 - this.level);
      }
    }

    this.trail.push(b.x, b.y);
    if (this.trail.length > 20) this.trail.splice(0, 2);

    if (b.y > this.h.height + 30) {
      if (--this.lives <= 0) {
        this.finish();
      } else {
        this.launch();
      }
    }
  }

  protected render(ctx: CanvasRenderingContext2D): void {
    const c = this.h.color;
    ctx.lineWidth = 2;
    for (const t of this.bumpers) {
      ctx.strokeStyle = t.hit ? "#ffffff" : c;
      ctx.globalAlpha = t.hit ? 1 : 0.8;
      ctx.shadowColor = c;
      ctx.shadowBlur = t.hit ? 24 : 8;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * TAU) / 6;
        const px = t.x + Math.cos(a) * (t.r + (t.hit ? t.hit * 0.5 : 0));
        const py = t.y + Math.sin(a) * (t.r + (t.hit ? t.hit * 0.5 : 0));
        if (i) ctx.lineTo(px, py);
        else ctx.moveTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    }
    ctx.shadowBlur = 0;
    const tr = this.trail;
    for (let i = 0; i < tr.length; i += 2) {
      ctx.globalAlpha = (i / tr.length) * 0.4;
      ctx.fillStyle = c;
      ctx.beginPath();
      ctx.arc(tr[i], tr[i + 1], this.ball.r * (i / tr.length), 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    glowDot(ctx, this.ball.x, this.ball.y, this.ball.r, "#ffffff", c, 22);
    const m = this.h.mouse;
    if (m.active) {
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = c;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(m.x, m.y, 56, 0, TAU);
      ctx.stroke();
    }
  }
}

// ------------------------------------------------------------------ 4. survival

interface Hunter {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

/** You are a dot that follows the cursor; red hunters chase it. Click for a pulse that shoves them back. */
class SurvivalGame extends BaseGame {
  private p = { x: 0, y: 0 };
  private trail: number[] = [];
  private hunters: Hunter[] = [];
  private frames = 0;
  private pulse = -1;
  private cooldown = 0;

  constructor(h: GameHost) {
    super(h, "survival");
  }

  protected init(): void {
    this.p = { x: this.h.width / 2, y: this.h.height * 0.2 };
    this.trail = [];
    this.hunters = [];
    this.frames = 0;
    this.pulse = -1;
    this.cooldown = 0;
    this.spawn();
  }

  protected extra(): string {
    return this.cooldown > 0 ? `${Math.ceil(this.cooldown / 60)}s` : "◉";
  }

  private spawn(): void {
    const w = this.h.width;
    const h = this.h.height;
    const side = Math.floor(Math.random() * 4);
    this.hunters.push({
      x: side === 0 ? 0 : side === 1 ? w : Math.random() * w,
      y: side === 2 ? 0 : side === 3 ? h : Math.random() * h,
      vx: 0,
      vy: 0,
    });
  }

  protected override down(): void {
    if (this.cooldown <= 0) {
      this.pulse = 0;
      this.cooldown = 300;
      for (const k of this.hunters) {
        const dx = k.x - this.p.x;
        const dy = k.y - this.p.y;
        const d = Math.hypot(dx, dy) || 1;
        if (d < 180) {
          k.vx += (dx / d) * 9;
          k.vy += (dy / d) * 9;
        }
      }
    }
  }

  protected update(): void {
    this.frames++;
    this.score = Math.floor(this.frames / 60);
    if (this.cooldown > 0) this.cooldown--;
    if (this.pulse >= 0 && ++this.pulse > 24) this.pulse = -1;
    if (this.frames % 420 === 0 && this.hunters.length < 14) this.spawn();

    const m = this.h.mouse;
    if (m.active) {
      this.p.x += (m.x - this.p.x) * 0.22;
      this.p.y += (m.y - this.p.y) * 0.22;
    }
    this.trail.push(this.p.x, this.p.y);
    if (this.trail.length > 24) this.trail.splice(0, 2);

    const speed = Math.min(3.2, 1.3 + this.hunters.length * 0.12);
    for (const k of this.hunters) {
      const dx = this.p.x - k.x;
      const dy = this.p.y - k.y;
      const d = Math.hypot(dx, dy) || 1;
      // steer toward the player with a limited turn rate, so they swing wide instead of homing exactly
      k.vx = k.vx * 0.94 + (dx / d) * speed * 0.06;
      k.vy = k.vy * 0.94 + (dy / d) * speed * 0.06;
      k.x += k.vx;
      k.y += k.vy;
      if (d < 14) {
        this.finish();
        return;
      }
    }
  }

  protected render(ctx: CanvasRenderingContext2D): void {
    const tr = this.trail;
    for (let i = 0; i < tr.length; i += 2) {
      ctx.globalAlpha = (i / tr.length) * 0.35;
      ctx.fillStyle = this.h.color;
      ctx.beginPath();
      ctx.arc(tr[i], tr[i + 1], 6 * (i / tr.length), 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    for (const k of this.hunters) {
      glowDot(ctx, k.x, k.y, 5 + Math.sin(this.h.tick * 0.2 + k.x) * 0.8, RED, RED, 14);
    }
    glowDot(ctx, this.p.x, this.p.y, 7, "#ffffff", this.h.color, 20);
    if (this.pulse >= 0) {
      ctx.strokeStyle = "#ffffff";
      ctx.globalAlpha = 1 - this.pulse / 24;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(this.p.x, this.p.y, 180 * (this.pulse / 24), 0, TAU);
      ctx.stroke();
    }
  }
}

// ------------------------------------------------------------------ 5. chain reaction

/** One click lights the particles in a small circle; the fire jumps from particle to particle. Reach the goal to go on. */
class ChainGame extends BaseGame {
  private phase: "aim" | "burn" | "next" = "aim";
  private round = 1;
  private goal = 50;
  private ignited = 0;
  private now = 0;
  private banner = 0;
  private preview = 0;
  private queue: { p: GameParticle; at: number }[] = [];
  private queued = new Set<GameParticle>();
  private rings: { x: number; y: number; age: number }[] = [];
  private static readonly R0 = 70;
  private static readonly JUMP = 26;

  constructor(h: GameHost) {
    super(h, "chain");
  }

  protected init(): void {
    this.phase = "aim";
    this.round = 1;
    this.goal = 50;
    this.reset();
  }

  private reset(): void {
    this.ignited = 0;
    this.now = 0;
    this.queue = [];
    this.queued.clear();
    this.rings = [];
  }

  protected extra(): string {
    return `${this.h.label("GENERAL.GAME_ROUND").toUpperCase()} ${this.round}   ${this.h.label("GENERAL.GAME_GOAL").toUpperCase()} ${this.ignited}/${this.goal}`;
  }

  protected override down(x: number, y: number): void {
    if (this.phase !== "aim") {
      return;
    }
    this.phase = "burn";
    for (const p of this.h.particles) {
      const d = Math.hypot(p.x - x, p.y - y);
      if (d < ChainGame.R0) {
        this.queue.push({ p, at: Math.round((d / ChainGame.R0) * 8) });
        this.queued.add(p);
      }
    }
    if (this.queue.length === 0) {
      // clicked on empty space: the round is spent
      this.finish();
    }
  }

  protected update(): void {
    const ps = this.h.particles;
    if (this.phase === "aim") {
      const m = this.h.mouse;
      if (this.h.tick % 3 === 0) {
        let n = 0;
        if (m.active) {
          for (const p of ps) {
            if (Math.abs(p.x - m.x) < ChainGame.R0 && Math.abs(p.y - m.y) < ChainGame.R0 && Math.hypot(p.x - m.x, p.y - m.y) < ChainGame.R0)
              n++;
          }
        }
        this.preview = n;
      }
      return;
    }
    if (this.phase === "next") {
      this.h.refill(60);
      if (--this.banner <= 0) {
        this.phase = "aim";
        this.reset();
      }
      return;
    }

    // burn
    this.now++;
    for (let i = this.rings.length - 1; i >= 0; i--) {
      if (++this.rings[i].age > 26) this.rings.splice(i, 1);
    }
    const due: GameParticle[] = [];
    const rest: { p: GameParticle; at: number }[] = [];
    for (const e of this.queue) {
      if (e.at <= this.now) due.push(e.p);
      else rest.push(e);
    }
    this.queue = rest;
    if (due.length) {
      const cell = ChainGame.JUMP;
      const cols = Math.ceil(this.h.width / cell) + 1;
      const rows = Math.ceil(this.h.height / cell) + 1;
      const head = new Int32Array(cols * rows).fill(-1);
      const next = new Int32Array(ps.length);
      for (let i = 0; i < ps.length; i++) {
        const c = Math.floor(ps[i].y / cell) * cols + Math.floor(ps[i].x / cell);
        next[i] = head[c];
        head[c] = i;
      }
      const burnt = new Set<GameParticle>(due);
      for (const p of due) {
        this.rings.push({ x: p.x, y: p.y, age: 0 });
        this.ignited++;
        this.score++;
        const cx = Math.floor(p.x / cell);
        const cy = Math.floor(p.y / cell);
        for (let gy = Math.max(0, cy - 1); gy <= Math.min(rows - 1, cy + 1); gy++) {
          for (let gx = Math.max(0, cx - 1); gx <= Math.min(cols - 1, cx + 1); gx++) {
            for (let j = head[gy * cols + gx]; j !== -1; j = next[j]) {
              const q = ps[j];
              if (this.queued.has(q) || Math.hypot(q.x - p.x, q.y - p.y) > ChainGame.JUMP || Math.random() > 0.6) continue;
              this.queued.add(q);
              this.queue.push({ p: q, at: this.now + 6 + Math.floor(Math.random() * 6) });
            }
          }
        }
      }
      // burnt particles leave the field (in place: the field owns the array)
      let k = 0;
      for (let i = 0; i < ps.length; i++) if (!burnt.has(ps[i])) ps[k++] = ps[i];
      ps.length = k;
    }

    if (this.queue.length === 0 && this.rings.length === 0) {
      if (this.ignited >= this.goal) {
        this.round++;
        this.goal += 40;
        this.phase = "next";
        this.banner = 100;
      } else {
        this.finish();
      }
    }
  }

  protected render(ctx: CanvasRenderingContext2D): void {
    const c = this.h.color;
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = GOLD;
    for (const r of this.rings) {
      ctx.globalAlpha = (1 - r.age / 26) * 0.9;
      ctx.beginPath();
      ctx.arc(r.x, r.y, 3 + r.age * 0.7, 0, TAU);
      ctx.stroke();
    }
    const m = this.h.mouse;
    if (this.phase === "aim" && m.active) {
      ctx.globalAlpha = 0.6;
      ctx.strokeStyle = c;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.arc(m.x, m.y, ChainGame.R0, 0, TAU);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = c;
      ctx.font = `600 12px ${MONO}`;
      ctx.textAlign = "center";
      ctx.fillText(String(this.preview), m.x, m.y - ChainGame.R0 - 8);
    }
    if (this.phase === "next") {
      ctx.globalAlpha = Math.min(1, this.banner / 30);
      ctx.fillStyle = c;
      ctx.font = `700 30px ${MONO}`;
      ctx.textAlign = "center";
      ctx.fillText(`${this.h.label("GENERAL.GAME_ROUND").toUpperCase()} ${this.round}`, this.h.width / 2, this.h.height * 0.2);
    }
  }
}

// ------------------------------------------------------------------ 6. gravity

/** Hold to pull the particles, release to fling them. Every few seconds the zone closes on whatever is inside it. */
class GravityGame extends BaseGame {
  private zone = { x: 0, y: 0, r: 90, tx: 0, ty: 0 };
  private cycle = 0;
  private level = 1;
  private inside = 0;
  private flash = 0;
  private static readonly CYCLE = 480;
  private static readonly PULL = 300;

  constructor(h: GameHost) {
    super(h, "gravity");
  }

  protected init(): void {
    this.level = 1;
    this.cycle = GravityGame.CYCLE;
    this.zone.r = 90;
    [this.zone.x, this.zone.y] = freePoint(this.h, 120);
    [this.zone.tx, this.zone.ty] = freePoint(this.h, 120);
    this.flash = 0;
  }

  private need(): number {
    return 15 + this.level * 8;
  }

  protected extra(): string {
    return `${this.h.label("GENERAL.GAME_GOAL").toUpperCase()} ${this.inside}/${this.need()}`;
  }

  protected override up(x: number, y: number): void {
    // fling: everything near the cursor takes its velocity
    const m = this.h.mouse;
    const fx = Math.max(-14, Math.min(14, m.vx * 0.9));
    const fy = Math.max(-14, Math.min(14, m.vy * 0.9));
    for (const p of this.h.particles) {
      if (Math.abs(p.x - x) < 220 && Math.abs(p.y - y) < 220 && Math.hypot(p.x - x, p.y - y) < 220) {
        p.vx += fx;
        p.vy += fy;
      }
    }
  }

  protected update(): void {
    const z = this.zone;
    const ps = this.h.particles;
    const m = this.h.mouse;

    // the zone drifts toward a waypoint clear of the card
    const dx = z.tx - z.x;
    const dy = z.ty - z.y;
    const d = Math.hypot(dx, dy);
    if (d < 6) {
      [z.tx, z.ty] = freePoint(this.h, 120);
    } else {
      const s = 0.8 + this.level * 0.1;
      z.x += (dx / d) * s;
      z.y += (dy / d) * s;
    }

    const pulling = this.h.held && m.active;
    let inside = 0;
    for (const p of ps) {
      if (pulling) {
        const ddx = m.x - p.x;
        const ddy = m.y - p.y;
        const dd2 = ddx * ddx + ddy * ddy;
        if (dd2 < GravityGame.PULL * GravityGame.PULL && dd2 > 1) {
          const dd = Math.sqrt(dd2);
          const f = (1 - dd / GravityGame.PULL) * 0.55;
          p.vx += (ddx / dd) * f;
          p.vy += (ddy / dd) * f;
          if (dd < 40) {
            p.vx *= 0.92;
            p.vy *= 0.92;
          }
        }
      }
      if ((p.x - z.x) ** 2 + (p.y - z.y) ** 2 < z.r * z.r) inside++;
    }
    this.inside = inside;
    if (this.flash > 0) this.flash--;

    if (--this.cycle <= 0) {
      if (inside < this.need()) {
        this.finish();
        return;
      }
      // the zone closes: what is inside is captured and leaves the field
      let k = 0;
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        if ((p.x - z.x) ** 2 + (p.y - z.y) ** 2 >= z.r * z.r) ps[k++] = p;
      }
      ps.length = k;
      this.score += inside;
      this.level++;
      this.flash = 30;
      z.r = Math.max(46, 90 - this.level * 5);
      this.cycle = GravityGame.CYCLE;
    }
    this.h.refill(10);
  }

  protected render(ctx: CanvasRenderingContext2D): void {
    const z = this.zone;
    const c = this.h.color;
    const ok = this.inside >= this.need();
    ctx.strokeStyle = ok ? GOLD : c;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.85;
    ctx.shadowColor = ok ? GOLD : c;
    ctx.shadowBlur = ok ? 20 : 8;
    ctx.beginPath();
    ctx.arc(z.x, z.y, z.r, 0, TAU);
    ctx.stroke();
    ctx.shadowBlur = 0;
    // the arc inside the ring is the time left before it closes
    ctx.globalAlpha = 0.9;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(z.x, z.y, z.r - 8, -Math.PI / 2, -Math.PI / 2 + TAU * (this.cycle / GravityGame.CYCLE));
    ctx.stroke();
    ctx.fillStyle = ok ? GOLD : c;
    ctx.font = `700 22px ${MONO}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(this.inside), z.x, z.y);
    if (this.flash > 0) {
      ctx.globalAlpha = this.flash / 30;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(z.x, z.y, z.r, 0, TAU);
      ctx.fill();
    }
  }
}

/** Builds the game for `id`; snake is not here, the field runs it itself. */
export function createGame(id: Exclude<GameId, "snake">, host: GameHost): Game {
  switch (id) {
    case "hunter":
      return new HunterGame(host);
    case "constellation":
      return new ConstellationGame(host);
    case "pinball":
      return new PinballGame(host);
    case "survival":
      return new SurvivalGame(host);
    case "chain":
      return new ChainGame(host);
    case "gravity":
      return new GravityGame(host);
  }
}
